import { matches, type OrderRow, type RfqListItem, type Site, type Supplier, t } from '@procumi/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { Route } from '../routes';

/**
 * Căutarea din toată aplicația: Ctrl K (⌘K pe Mac) sau butonul de pe panou.
 * Cererile se caută pe server (au și pozițiile); furnizorii, șantierele și
 * comenzile se aduc o dată la deschidere și se filtrează aici, cu `matches`
 * (fără diacritice, cuvintele în orice ordine).
 */

const OPEN = 'procumi:search';
export const openSearch = () => window.dispatchEvent(new CustomEvent(OPEN));

interface Hit {
  key: string;
  group: string;
  title: string;
  sub: string;
  to: Route;
}

export function CommandSearch({ go }: { go: (r: Route) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [rfqs, setRfqs] = useState<RfqListItem[]>([]);
  const [lists, setLists] = useState<{ sup: Supplier[]; sites: Site[]; orders: OrderRow[] } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const dlg = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    const on = () => setOpen(true);
    window.addEventListener('keydown', key);
    window.addEventListener(OPEN, on);
    return () => {
      window.removeEventListener('keydown', key);
      window.removeEventListener(OPEN, on);
    };
  }, []);

  useEffect(() => {
    const d = dlg.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setQ('');
      setSel(0);
      input.current?.focus();
      if (!lists) {
        Promise.all([api.suppliers(), api.sites({ includeClosed: true }), api.orders()])
          .then(([sup, sites, orders]) => setLists({ sup, sites, orders }))
          .catch(() => setLists({ sup: [], sites: [], orders: [] }));
      }
    } else if (!open && d.open) d.close();
  }, [open, lists]);

  // cererile: pe server, după ce omul se oprește din scris
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(
      () => {
        const text = q.trim() || undefined;
        // deschise întâi, apoi cele încheiate: căutarea găsește și cererile vechi
        Promise.all([
          api.rfqs({ q: text, state: 'open', limit: 6 }),
          api.rfqs({ q: text, state: 'closed', limit: 4 }),
        ])
          .then(([a, b]) => setRfqs([...a.rows, ...b.rows].slice(0, 7)))
          .catch(() => setRfqs([]));
      },
      q ? 180 : 0,
    );
    return () => window.clearTimeout(t);
  }, [q, open]);

  const hits = useMemo<Hit[]>(() => {
    const out: Hit[] = rfqs.map((r) => ({
      key: `r-${r.id}`,
      group: t('Cereri de ofertă'),
      title: r.title,
      sub: t(
        '{0} · {1} · {2} din {3} oferte',
        r.site.name,
        r.category,
        r.sends.filter((s) => s.hasOffer).length,
        r.sends.length,
      ),
      to: { view: 'rfq', id: r.id },
    }));
    if (!q.trim() || !lists) return out;
    const take = <T,>(rows: T[], text: (x: T) => string, n = 4) =>
      rows.filter((x) => matches(text(x), q)).slice(0, n);
    for (const s of take(lists.sup, (x) => `${x.name} ${x.categories.join(' ')}`)) {
      out.push({
        key: `s-${s.id}`,
        group: t('Furnizori'),
        title: s.name,
        sub: s.categories.join(', '),
        to: { view: 'suppliers' },
      });
    }
    for (const s of take(lists.sites, (x) => `${x.name} ${x.locality} ${x.client}`)) {
      out.push({
        key: `p-${s.id}`,
        group: t('Șantiere'),
        title: s.name,
        sub: [s.locality, s.closedAt ? t('închis') : 'activ'].filter(Boolean).join(' · '),
        to: { view: 'site', id: s.id },
      });
    }
    for (const o of take(
      lists.orders,
      (x) => `${x.number} ${x.supplier.name} ${x.rfqTitle} ${x.site.name}`,
    )) {
      out.push({
        key: `o-${o.id}`,
        group: t('Comenzi'),
        title: `${o.number} · ${o.supplier.name}`,
        sub: `${o.rfqTitle} · ${o.site.name}`,
        to: { view: 'order', rfqId: o.rfqId, orderId: o.id },
      });
    }
    return out;
  }, [rfqs, lists, q]);

  const pick = (h: Hit | undefined) => {
    if (!h) return;
    setOpen(false);
    go(h.to);
  };

  let last = '';
  return (
    <dialog
      ref={dlg}
      className="cmdk"
      onClose={() => setOpen(false)}
      onClick={(e) => e.target === dlg.current && setOpen(false)}
    >
      <div className="cmdk-in">
        <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M7.2 12.4a5.2 5.2 0 1 0 0-10.4 5.2 5.2 0 0 0 0 10.4zM14 14l-3.1-3.1"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
        <input
          ref={input}
          value={q}
          placeholder={t('Caută cereri, furnizori, șantiere, comenzi…')}
          aria-label={t('Caută')}
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel(Math.min(sel + 1, hits.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel(Math.max(sel - 1, 0));
            } else if (e.key === 'Enter') pick(hits[sel]);
          }}
        />
        <kbd>{t('Esc')}</kbd>
      </div>
      <div className="cmdk-list" role="listbox">
        {hits.length ? (
          hits.map((h, i) => {
            const head = h.group !== last ? <div className="cmdk-g">{h.group}</div> : null;
            last = h.group;
            return (
              <div key={h.key}>
                {head}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === sel}
                  className="cmdk-row"
                  onMouseEnter={() => setSel(i)}
                  onClick={() => pick(h)}
                >
                  <b>{h.title}</b>
                  <span>{h.sub}</span>
                </button>
              </div>
            );
          })
        ) : (
          <p className="cmdk-none">{q ? t('Nimic pentru „{0}”.', q) : t('Scrie ce cauți.')}</p>
        )}
      </div>
      <div className="cmdk-f">
        <span>
          <kbd>↑</kbd> <kbd>↓</kbd> {t('alegi')}
        </span>
        <span>
          <kbd>{t('Enter')}</kbd> {t('deschizi')}
        </span>
      </div>
    </dialog>
  );
}
